import { Link } from "react-router-dom";

export default function TermsOfServicePage() {
  document.title = "Terms of Service | ROAS-AI";
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-300 py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/login" className="text-violet-400 hover:text-violet-300 text-sm mb-8 inline-block">&larr; Back to ROAS-AI</Link>

        <h1 className="font-heading text-4xl text-white font-bold mb-2">Terms of Service</h1>
        <p className="text-zinc-400 mb-10">Last updated: March 2, 2026</p>

        <div className="space-y-8 text-sm leading-relaxed">
          <section>
            <h2 className="text-white text-lg font-semibold mb-3">1. Acceptance of Terms</h2>
            <p>By accessing or using ROAS-AI at roas-ai.com (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">2. Description of Service</h2>
            <p>ROAS-AI is a marketing campaign optimization platform that connects to advertising platforms (Meta Ads, Google Ads, LinkedIn Ads, Microsoft Advertising) via authorized OAuth connections to provide cross-platform analytics, AI-powered budget optimization, and campaign management tools.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">3. Account Registration</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You must provide accurate and complete information when creating an account.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>Account access requires admin approval. We reserve the right to approve or deny accounts at our discretion.</li>
              <li>You must be at least 18 years old to use the Service.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">4. Ad Platform Connections</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You authorize ROAS-AI to access your ad platform data through OAuth connections you initiate.</li>
              <li>You are responsible for ensuring you have the authority to connect the ad accounts you link to the Service.</li>
              <li>We access data solely to provide the optimization and analytics features of the Service.</li>
              <li>You may disconnect any ad platform at any time, which immediately revokes our access.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">5. AI-Powered Features</h2>
            <p>The Service includes AI-powered features such as budget recommendations and campaign suggestions. These are advisory in nature. You acknowledge that:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>AI recommendations are generated based on available data and may not always be optimal.</li>
              <li>You are solely responsible for any budget changes or campaign decisions made based on AI suggestions.</li>
              <li>AI features consume tokens, which can be purchased through the Service.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">6. Payments</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Certain features require payment, including campaign budget packages and AI tokens.</li>
              <li>Payments are processed securely through Stripe.</li>
              <li>All purchases are final unless otherwise stated.</li>
              <li>Prices are subject to change with reasonable notice.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">7. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to the Service or its systems</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Use the Service to violate any ad platform's terms of service</li>
              <li>Share your account credentials with unauthorized third parties</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">8. Intellectual Property</h2>
            <p>The Service, including its design, features, and content, is owned by ROAS-AI and protected by intellectual property laws. You retain ownership of your data and ad platform content accessed through the Service.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">9. Limitation of Liability</h2>
            <p>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, ROAS-AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE, INCLUDING BUT NOT LIMITED TO LOSS OF AD REVENUE, BUDGET MISALLOCATION, OR DATA LOSS.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">10. Indemnification</h2>
            <p>You agree to indemnify and hold harmless ROAS-AI from any claims, damages, or expenses arising from your use of the Service, your violation of these Terms, or your violation of any third-party rights.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">11. Termination</h2>
            <p>We may suspend or terminate your access to the Service at any time for violations of these Terms or for any other reason. Upon termination, your right to use the Service ceases immediately. Sections regarding limitation of liability and indemnification survive termination.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">12. Changes to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the revised Terms.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">13. Contact</h2>
            <p>For questions about these Terms, contact us at <a href="mailto:legal@roas-ai.com" className="text-violet-400 hover:text-violet-300">legal@roas-ai.com</a> or write to us at: 701 Tillery Street, Unit 12-1574, Austin, Texas 78702, United States.</p>
          </section>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 text-zinc-500 text-xs text-right">
          <a href="/about" className="text-zinc-400 hover:text-zinc-400 mr-4">About Us</a>
          <a href="/privacy" className="text-zinc-400 hover:text-zinc-400 mr-4">Privacy</a>
          Copyright (c) 2026 Beruf LLC
        </div>
      </div>
    </div>
  );
}
